import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import type { FilterColumnOptions } from "@onengine/component-library";
import { useTableDropdown } from "@/hooks/tableDropdown/useTableDropdown";
import { DEBOUNCED_WAIT_TIME } from "@/components/data/constants";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import type { FilterEntry } from "@/utils/buildFilterByParam";
import { decodeDateRangeLabel, decodeNumericRangeLabel } from "@/hooks/table/tempDateFilterLabel";

type FilterMenuVariant = "list" | "badge" | "date" | "datetime" | "numerical";

type TableDropdownStoreType =
  | "clients"
  | "entities"
  | "serviceProviders"
  | "assets"
  | "workOrders"
  | "users"
  | "roles"
  | "brands"
  | "locations"
  | "contacts"
  | "forms";

export type FilterOption = { id: string; label: string };

export type FilterOptionsPage = {
  items: FilterOption[];
  nextCursor: string | null;
  hasMore: boolean;
};

/**
 * Cursor-paginated source for a filter column whose options can't be fetched in
 * a single bounded request (e.g. WO addresses). The shim owns the accumulate /
 * dedupe / cursor state; the column only supplies the page fetcher, so the shim
 * stays decoupled from any specific dropdown API.
 */
export type InfiniteScrollSource = {
  fetchPage: (args: { search: string; cursor: string | null }) => Promise<FilterOptionsPage>;
};

/**
 * One entry per filterable column. Replaces the old `TColumn.searchParams` +
 * `filterStoreType` wiring: it tells the shim which dropdown API to hit (async
 * `list`) or which fixed options to expose (`staticItems`), and how to map the
 * column's display label back to the backend `filterBy` param.
 *
 * `datetime` columns are intentionally out of scope here — they need their own
 * label↔ISO-range encode/decode and are handled as a separate follow-up.
 */
export type TableFilterColumnConfig = {
  columnId: string;
  filterByKey: string;
  variant?: FilterMenuVariant;
  storeType?: TableDropdownStoreType;
  searchParams?: TSearchParams;
  staticItems?: FilterOption[];
  /**
   * Which part of the selected option to send as the backend filter value:
   * "id" (default) for id-keyed APIs (e.g. `visitTypeId`), "label" for APIs that
   * match the display string (e.g. SP `name`/`trades`/`address`).
   */
  valueSource?: "id" | "label";
  /**
   * When set, the column loads options via cursor-based infinite scroll instead
   * of the single bounded `useTableDropdown` request. Mutually exclusive with
   * `searchParams` for option fetching.
   */
  infiniteScroll?: InfiniteScrollSource;
};

export type UseTableFilterOptionsOptions = {
  enabled?: boolean;
  /** Default store type for `list` columns that don't set their own. */
  defaultStoreType?: TableDropdownStoreType;
  /**
   * Seeds the label→id map for selections restored before any chip is opened
   * (e.g. from URL/preference persistence in Phase 2). Without this, a restored
   * filter label cannot be resolved to its backend id until the user reopens
   * the chip. The shim owns this map as the source of truth for label↔id.
   */
  initialAppliedOptions?: Record<string, FilterOption[]>;
};

export type UseTableFilterOptionsResult = {
  filterOptions: Record<string, FilterColumnOptions>;
  onFetchFilterOptions: (columnId: string) => void;
  onSearchFilterOptions: (columnId: string, query: string) => void;
  onLoadMoreFilterOptions: (columnId: string, cursor: string | null) => void;
  /**
   * Converts the new Table's label-based filters into the `FilterEntry[]` that
   * `buildFilterByParam` / the list query layer already consume. Backend value
   * comes from `filterValue.id`; the label→id mapping is resolved from options
   * loaded this session plus any `initialAppliedOptions` seed.
   */
  toFilterEntries: (filters: Record<string, string[]>) => FilterEntry[];
  /**
   * Imperatively seeds the label→id map for selections restored after mount
   * (e.g. backend view-preferences, which load asynchronously). The construction
   * option `initialAppliedOptions` covers the synchronous URL case; this covers
   * the async case. Both keep the shim the single source of truth for label↔id.
   */
  seedAppliedOptions: (options: Record<string, FilterOption[]>) => void;
};

const isListVariant = (variant?: FilterMenuVariant) => (variant ?? "list") === "list";

const extractArray = (raw: unknown): unknown[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  const top = (raw as { data?: unknown }).data;
  if (Array.isArray(top)) return top;
  if (top && Array.isArray((top as { data?: unknown }).data)) {
    return (top as { data: unknown[] }).data;
  }
  return [];
};

const normalizeOptions = (raw: unknown, fields: string[]): FilterOption[] => {
  const field = fields[0];
  return extractArray(raw).map((item) => {
    if (typeof item === "string") return { id: item, label: item };
    const record = item as Record<string, unknown>;
    const label = String(field ? record[field] : (record.label ?? record.value ?? ""));
    const id = String(record.id ?? record.value ?? label);
    return { id, label };
  });
};

export function useTableFilterOptions(
  columns: TableFilterColumnConfig[],
  options?: UseTableFilterOptionsOptions,
): UseTableFilterOptionsResult {
  const { enabled = true, defaultStoreType, initialAppliedOptions } = options ?? {};

  const configByColumn = useMemo(() => {
    const map = new Map<string, TableFilterColumnConfig>();
    columns.forEach((col) => map.set(col.columnId, col));
    return map;
  }, [columns]);

  // label → id, accumulated across the session and never evicted, so an
  // in-session selection always resolves in toFilterEntries. Seeded with static
  // items and any restored selections.
  const labelToIdRef = useRef<Map<string, Map<string, string>>>(new Map());
  const recordOptions = useCallback((columnId: string, opts: FilterOption[]) => {
    let columnMap = labelToIdRef.current.get(columnId);
    if (!columnMap) {
      columnMap = new Map();
      labelToIdRef.current.set(columnId, columnMap);
    }
    opts.forEach(({ label, id }) => columnMap!.set(label, id));
  }, []);

  // Seed synchronously on first render so toFilterEntries resolves restored
  // labels to backend ids before the useEffect below fires (after render).
  const hasSeedRef = useRef(false);
  if (!hasSeedRef.current) {
    hasSeedRef.current = true;
    columns.forEach((col) => {
      if (col.staticItems) recordOptions(col.columnId, col.staticItems);
    });
    if (initialAppliedOptions) {
      Object.entries(initialAppliedOptions).forEach(([columnId, opts]) =>
        recordOptions(columnId, opts),
      );
    }
  }

  const staticOptions = useMemo(() => {
    const result: Record<string, FilterColumnOptions> = {};
    columns.forEach((col) => {
      if (col.staticItems) {
        result[col.columnId] = { items: col.staticItems.map((o) => o.label), loading: false };
      }
    });
    return result;
  }, [columns]);

  useEffect(() => {
    columns.forEach((col) => {
      if (col.staticItems) recordOptions(col.columnId, col.staticItems);
    });
    if (initialAppliedOptions) {
      Object.entries(initialAppliedOptions).forEach(([columnId, opts]) =>
        recordOptions(columnId, opts),
      );
    }
  }, [columns, initialAppliedOptions, recordOptions]);

  const [optionsByColumn, setOptionsByColumn] = useState<Record<string, FilterColumnOptions>>({});
  const [infiniteByColumn, setInfiniteByColumn] = useState<Record<string, FilterColumnOptions>>({});
  const [active, setActive] = useState<{ columnId: string | null; search: string }>({
    columnId: null,
    search: "",
  });
  const [debouncedSearch] = useDebouncedValue(active.search, DEBOUNCED_WAIT_TIME);
  const effectiveSearch = active.search === "" ? "" : debouncedSearch;

  const activeConfig = active.columnId ? configByColumn.get(active.columnId) : undefined;
  const isActiveInfinite = !!activeConfig?.infiniteScroll;
  const isActiveAsyncList =
    !!activeConfig &&
    isListVariant(activeConfig.variant) &&
    !!activeConfig.searchParams &&
    !isActiveInfinite;

  const { data: dropdownData, isFetching } = useTableDropdown({
    searchTerm: effectiveSearch.trim(),
    searchParams: activeConfig?.searchParams,
    label: "",
    enabled: enabled && isActiveAsyncList,
    storeType: activeConfig?.storeType ?? defaultStoreType ?? "clients",
  });

  useEffect(() => {
    if (!active.columnId || !isActiveAsyncList || isFetching) return;
    const normalized = normalizeOptions(dropdownData, activeConfig?.searchParams?.fields ?? []);
    recordOptions(active.columnId, normalized);
    setOptionsByColumn((prev) => ({
      ...prev,
      [active.columnId as string]: { items: normalized.map((o) => o.label), loading: false },
    }));
  }, [dropdownData, isFetching, active.columnId, isActiveAsyncList, activeConfig, recordOptions]);

  // Cursor-paginated fetch for infinite-scroll columns. A monotonic token guards
  // against out-of-order responses when search/cursor change mid-flight.
  const infiniteSeqRef = useRef(0);
  const fetchInfinitePage = useCallback(
    async (
      columnId: string,
      source: InfiniteScrollSource,
      search: string,
      cursor: string | null,
      append: boolean,
    ) => {
      const seq = ++infiniteSeqRef.current;
      setInfiniteByColumn((prev) => ({
        ...prev,
        [columnId]: { ...(prev[columnId] ?? { items: [] }), loading: true },
      }));
      try {
        const page = await source.fetchPage({ search, cursor });
        if (seq !== infiniteSeqRef.current) return;
        recordOptions(columnId, page.items);
        setInfiniteByColumn((prev) => {
          const existing = append ? (prev[columnId]?.items ?? []) : [];
          const seen = new Set(existing);
          const merged = [
            ...existing,
            ...page.items.map((o) => o.label).filter((l) => !seen.has(l)),
          ];
          return {
            ...prev,
            [columnId]: {
              items: merged,
              nextCursor: page.nextCursor,
              hasMore: page.hasMore,
              loading: false,
            },
          };
        });
      } catch {
        if (seq !== infiniteSeqRef.current) return;
        setInfiniteByColumn((prev) => ({
          ...prev,
          [columnId]: { ...(prev[columnId] ?? { items: [] }), loading: false },
        }));
      }
    },
    [recordOptions],
  );

  // First page on open, and refetch on (debounced) search — cursor null, never appends.
  useEffect(() => {
    if (!active.columnId || !isActiveInfinite || !activeConfig?.infiniteScroll) return;
    fetchInfinitePage(
      active.columnId,
      activeConfig.infiniteScroll,
      effectiveSearch.trim(),
      null,
      false,
    );
  }, [active.columnId, isActiveInfinite, activeConfig, effectiveSearch, fetchInfinitePage]);

  const filterOptions = useMemo(() => {
    const result: Record<string, FilterColumnOptions> = {
      ...staticOptions,
      ...optionsByColumn,
      ...infiniteByColumn,
    };
    if (active.columnId && isActiveAsyncList) {
      result[active.columnId] = {
        ...(result[active.columnId] ?? {}),
        loading: isFetching,
      };
    }
    return result;
  }, [
    staticOptions,
    optionsByColumn,
    infiniteByColumn,
    active.columnId,
    isActiveAsyncList,
    isFetching,
  ]);

  const onFetchFilterOptions = useCallback((columnId: string) => {
    setActive({ columnId, search: "" });
  }, []);

  const onSearchFilterOptions = useCallback((columnId: string, query: string) => {
    setActive({ columnId, search: query });
  }, []);

  const onLoadMoreFilterOptions = useCallback(
    (columnId: string, cursor: string | null) => {
      const config = configByColumn.get(columnId);
      const state = infiniteByColumn[columnId];
      if (!config?.infiniteScroll || !state?.hasMore || state.loading) return;
      fetchInfinitePage(columnId, config.infiniteScroll, active.search.trim(), cursor, true);
    },
    [configByColumn, infiniteByColumn, fetchInfinitePage, active.search],
  );

  const toFilterEntries = useCallback(
    (filters: Record<string, string[]>): FilterEntry[] => {
      const entries: FilterEntry[] = [];
      Object.entries(filters).forEach(([columnId, labels]) => {
        if (!labels?.length) return;
        const config = configByColumn.get(columnId);
        if (!config) return;
        const columnMap = labelToIdRef.current.get(columnId);
        const isDateVariant = config.variant === "date" || config.variant === "datetime";
        const isNumerical = config.variant === "numerical";
        const filterBy = config.filterByKey || columnId;
        labels.forEach((label) => {
          // TEMPORARY: date/numerical chips report a display label, not a structured
          // value — decode it here until the lib emits one (see tempDateFilterLabel).
          if (isDateVariant) {
            const range = decodeDateRangeLabel(label);
            if (!range) return;
            entries.push({
              filterBy,
              filterValue: { id: range, value: range, label },
            });
            return;
          }
          if (isNumerical) {
            const range = decodeNumericRangeLabel(label);
            if (!range) return;
            entries.push({
              filterBy,
              filterValue: { id: range, value: range, label },
            });
            return;
          }
          // A static column's id comes from its own config, which is authoritative:
          // a stale id restored from a saved filter (e.g. a dropdown row id persisted
          // before the column became static) must not win over the configured value.
          const staticId = config.staticItems?.find((o) => o.label === label)?.id;
          const id = staticId ?? columnMap?.get(label) ?? label;
          const value = config.valueSource === "label" ? label : id;
          entries.push({
            filterBy,
            filterValue: { id, value, label },
          });
        });
      });
      return entries;
    },
    [configByColumn],
  );

  const seedAppliedOptions = useCallback(
    (seed: Record<string, FilterOption[]>) => {
      Object.entries(seed).forEach(([columnId, opts]) => recordOptions(columnId, opts));
    },
    [recordOptions],
  );

  return {
    filterOptions,
    onFetchFilterOptions,
    onSearchFilterOptions,
    onLoadMoreFilterOptions,
    toFilterEntries,
    seedAppliedOptions,
  };
}
